import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

import { XMarkIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { AvailabilityReportItem } from '@/types/availability.types';
import { Button } from '@/components/ui/Button';
import { clsx } from 'clsx';
import {
  useCreateTradeForm,
  SearchInventoryItem,
} from '../hooks/useCreateTradeForm';
import { getFxRateInlineWarning } from '@/services/trades/trade-validation';
import { showsClosingCash } from '@/utils/trades/closing-cash';
import type { TradeType } from '@/types';
import {
  SecurityDetailsSection,
  AccountDetailsSection,
  PositionDetailsSection,
  NonCashCollateralSection,
  RateDetailsSection,
  PositionDatesSection,
  SettlementDetailsSection,
  TradeSummarySection,
  CloseConfirmationDialog,
  inputClassName,
  labelClassName,
} from './trade-form-sections';

type CreateTradeModalProps = Record<string, never>;

// Out-of-band FX rates surface an inline warning rather than a hard
// validation error. Band imported from trade-validation so the inline
// warning and the submit-time `validateTradeEntry` banner always agree
// (LR-371).

type SectionState = {
  securityDetails: boolean;
  accountDetails: boolean;
  positionDetails: boolean;
  nonCashCollateral: boolean;
  rateDetails: boolean;
  positionDates: boolean;
  settlementDetails: boolean;
  tradeSummary: boolean;
};

type SearchItem = {
  item: SearchInventoryItem;
  groupedItem: AvailabilityReportItem;
};

type SecurityDropdownItemProps = {
  item: SearchInventoryItem;
  groupedItem: AvailabilityReportItem;
  onSelect: (searchItem: SearchItem) => void;
};

const SecurityDropdownItem: React.FC<SecurityDropdownItemProps> = React.memo(({ item, groupedItem, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect({ item, groupedItem });
  }, [item, groupedItem, onSelect]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {item.shortName && item.shortName !== item.symbolId ? item.shortName : item.symbolId}
          </div>
          <div className="text-sm text-gray-500">
            <span className="">{item.symbolId}</span>
            {' • '}
            <span className="">{item.isin}</span>
            {item.sedol && (
              <>
                {' • '}
                <span className="">{item.sedol}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right ml-4">
          <div className="text-sm font-medium text-gray-900">
            {item.quantity.toLocaleString()} {item.currency || 'USD'}
          </div>
          <div className={`text-xs ${item.benchmarkFee < 0 ? 'text-red-600' : 'text-gray-900'}`}>
            Fee: {item.benchmarkFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>
    </button>
  );
});
SecurityDropdownItem.displayName = 'SecurityDropdownItem';

export const CreateTradeModal: React.FC<CreateTradeModalProps> = () => {
  const {
    // State
    isOpen,
    isCreating,
    selectedInventoryItem,
    securitySearchQuery,
    showSecurityDropdown,
    dropdownPosition,
    isSubmitted,
    validationErrors,
    showCloseConfirmation,
    hasFormChanges,
    sectionStates,
    isDataLoading,
    isFormDisabled,
    securityCurrency,
    effectiveSettlementCurrency,
    isCrossCurrency,
    isCashMatchingMode,
    isUsingFallbackRates,

    // Refs
    securityDropdownRef,
    securityInputRef,

    // Data
    counterparties,
    custodians,
    custodianRequired,
    custodiansEmpty,
    custodiansError,
    counterpartiesEmpty,
    counterpartiesError,
    benchmarkRates,
    benchmarkMeta,
    filteredInventoryItems,
    calculatedValues,
    totalRate,
    availableTradeTypes,
    tradeTypeLabels,
    tradeType,

    // Form
    form,

    // Form field values
    rateType,
    rateEntryMode,
    tradeDuration,
    settlementDate,
    counterpartyName,
    deliveryType,
    settlementVenue,
    collateralType,
    collateralQuantity,
    collateralCurrentMarketPrice,
    collateralPercent,
    fxRate,

    // Holiday checks
    tradeDateHoliday,
    settlementDateHoliday,

    // Handlers
    handleSelectSecurity,
    handleClearSecurity,
    handleClose,
    handleCloseWithoutConfirmation,
    handleBackdropClick,
    toggleSection,
    setSecuritySearchQuery,
    setShowSecurityDropdown,
    setIsSubmitted,
    setShowCloseConfirmation,
    setHasFormChanges,

  } = useCreateTradeForm();

  // Inline warning under the FX Rate input for out-of-band rates.
  // Non-blocking — submit proceeds regardless. Logic lives in
  // trade-validation so it stays in sync with the submit-time banner.
  const fxRateWarning = getFxRateInlineWarning(fxRate);

  const showClosingSettlement = showsClosingCash({
    trade_type: tradeType as TradeType | undefined,
  });

  // Drives the live validation summary's styling: a blocking-error (red)
  // banner when any error is present, otherwise an advisory warnings-only
  // (amber) banner. Both severities surface pre-submit (LR-452); only the
  // colour/heading differ.
  const hasBlockingValidationError = validationErrors.some((error) => error.severity === 'error');

  const SectionHeader: React.FC<{ title: string; section: keyof SectionState }> = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full py-3 px-4 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 transition-colors"
    >
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {sectionStates[section] ? (
        <ChevronUpIcon className="h-5 w-5 text-gray-500" />
      ) : (
        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
      )}
    </button>
  );
  return (
    <>
    {/* `<Dialog static>` keeps its portal mounted regardless of `Transition
        show`, so a closed modal would otherwise leave an inert DOM subtree
        that can intercept focus / pointer events. The outer `isOpen` guard
        is what actually unmounts the dialog when closed (one of the LR-316
        modal-blocker fixes). */}
    {isOpen && (
    <Transition show={isOpen} as={React.Fragment}>
      {/* LR-331: z-index baseline must exceed FilterSidebar (z-[9998]).
          Otherwise the right Filters/Columns sidebar visually clips the
          modal on 100% zoom at 1600px (reproduced on stage 0.10.0).
          Backdrop / overlay container / panel stack above sidebar:
          9999 → 10000 → 10001. */}
      <Dialog as="div" className="relative z-[9999]" onClose={() => {}} static initialFocus={securityInputRef}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 z-[9999]" onClick={handleBackdropClick} />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto pointer-events-none z-[10000]">
          <div className="flex min-h-full items-start justify-center py-5 pointer-events-none" onClick={handleBackdropClick}>
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className="relative w-full max-w-7xl max-h-[calc(100vh-40px)] transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col pointer-events-auto z-[10001]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <DialogTitle className="text-lg font-semibold text-gray-900">
                    Create Trade Entry
                  </DialogTitle>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit();
                  }}
                  onChange={() => {
                    if (!hasFormChanges) setHasFormChanges(true);
                  }}
                  className="flex-1 overflow-y-auto"
                >
                  <div className="divide-y divide-gray-200">
                    {/* Security Search */}
                    <div data-tour="trade-form-section-security" className="p-6">
                      <label className={labelClassName}>
                        Security <span className="text-red-500">*</span>
                      </label>
                      <form.Field
                        name="symbol_id"
                        validators={{
                          onChange: ({ value }) => {
                            if (!value || !selectedInventoryItem) return 'Security is required';
                            return undefined;
                          },
                        }}
                      >
                        {(field) => {
                          const isFormDisabled = !selectedInventoryItem;
                          return (
                            <>
                              {selectedInventoryItem ? (
                                <div className={`relative w-full min-h-[42px] px-3 py-2 pl-10 bg-white border rounded transition-all ${
                                  isFormDisabled
                                    ? 'border-blue-500 ring-2 ring-blue-300 ring-opacity-50 shadow-sm'
                                    : 'border-gray-300'
                                }`}>
                                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-400 rounded-md">
                                    <span className="text-sm font-medium text-gray-900">
                                      {selectedInventoryItem.shortName && selectedInventoryItem.shortName !== selectedInventoryItem.symbolId
                                        ? `${selectedInventoryItem.shortName} (${selectedInventoryItem.symbolId})`
                                        : selectedInventoryItem.symbolId}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearSecurity();
                                      }}
                                      onMouseDown={(e) => e.preventDefault()}
                                      className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                                      title="Clear selection"
                                    >
                                      <XMarkIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                                    <input
                                      ref={securityInputRef}
                                      type="text"
                                      value={securitySearchQuery}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setSecuritySearchQuery(value);
                                        setShowSecurityDropdown(value.trim().length > 0);
                                      }}
                                      onFocus={() => {
                                        if (securitySearchQuery.trim().length > 0) {
                                          setShowSecurityDropdown(true);
                                        }
                                      }}
                                      placeholder={isDataLoading ? "Loading securities..." : "Search by Symbol, ISIN, SEDOL, or Name..."}
                                      className={clsx(
                                        'w-full pl-10 pr-10 py-2 text-sm border rounded focus:ring-blue-500 focus:border-blue-500',
                                        isSubmitted && field.state.meta.errors.length > 0
                                          ? 'border-red-500'
                                          : 'border-gray-300'
                                      )}
                                    />
                                    <ChevronDownIcon className={clsx(
                                      'absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 transition-transform pointer-events-none',
                                      showSecurityDropdown && 'rotate-180'
                                    )} />
                                  </div>
                                </>
                              )}
                              {showSecurityDropdown && !selectedInventoryItem && securitySearchQuery.trim().length > 0 && createPortal(
                                <div
                                  ref={securityDropdownRef}
                                  className="fixed z-[10000] bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto pointer-events-auto"
                                  style={{
                                    top: `${dropdownPosition.top + 4}px`,
                                    left: `${dropdownPosition.left}px`,
                                    width: `${dropdownPosition.width}px`,
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {isDataLoading ? (
                                    <div className="px-4 py-8 text-center text-gray-500">
                                      <div className="flex items-center justify-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
                                        <span>Loading securities...</span>
                                      </div>
                                    </div>
                                  ) : filteredInventoryItems.length > 0 ? (
                                    <div className="py-1">
                                      {filteredInventoryItems.map(({ item, groupedItem }, idx) => (
                                        <SecurityDropdownItem
                                          key={`${item.symbolId}-${idx}`}
                                          item={item}
                                          groupedItem={groupedItem}
                                          onSelect={handleSelectSecurity}
                                        />
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="px-4 py-8 text-center text-gray-500">
                                      {securitySearchQuery.trim().length > 0
                                        ? 'No securities found'
                                        : 'Start typing to search...'}
                                    </div>
                                  )}
                                </div>,
                                document.body
                              )}
                              {isSubmitted && field.state.meta.errors.length > 0 && (
                                <p className="mt-1 text-sm text-red-600">{field.state.meta.errors[0]}</p>
                              )}
                            </>
                          );
                        }}
                      </form.Field>
                    </div>

                    {/* Security Details */}
                    {selectedInventoryItem && (
                      <div>
                        <SectionHeader title="Security Details" section="securityDetails" />
                        {sectionStates.securityDetails && (
                          <SecurityDetailsSection security={selectedInventoryItem} />
                        )}
                      </div>
                    )}

                    {/* Account Details */}
                    <div data-tour="trade-form-section-account">
                      <SectionHeader title="Account Details" section="accountDetails" />
                      {sectionStates.accountDetails && (
                        <AccountDetailsSection
                          form={form}
                          isSubmitted={isSubmitted}
                          selectedInventoryItem={selectedInventoryItem}
                          counterparties={counterparties}
                          custodians={custodians}
                          custodianRequired={custodianRequired}
                          custodiansEmpty={custodiansEmpty}
                          custodiansError={custodiansError}
                          counterpartiesEmpty={counterpartiesEmpty}
                          counterpartiesError={counterpartiesError}
                        />
                      )}
                    </div>

                    {/* Position Details */}
                    <div data-tour="trade-form-section-position" className={clsx('bg-white border border-gray-200 rounded-lg', !selectedInventoryItem && 'opacity-50')}>
                      <SectionHeader title="Position Details" section="positionDetails" />
                      {sectionStates.positionDetails && (
                        <PositionDetailsSection
                          form={form}
                          isSubmitted={isSubmitted}
                          selectedInventoryItem={selectedInventoryItem}
                          securityCurrency={securityCurrency}
                          calculatedValues={calculatedValues}
                          availableTradeTypes={availableTradeTypes}
                          tradeTypeLabels={tradeTypeLabels}
                        />
                      )}
                    </div>

                    {/* Non-cash Collateral Details */}
                    {collateralType === 'non_cash' && (
                      <div>
                        <SectionHeader title="Non-cash Collateral Details" section="nonCashCollateral" />
                        {sectionStates.nonCashCollateral && (
                          <NonCashCollateralSection
                            form={form}
                            isSubmitted={isSubmitted}
                            custodians={custodians}
                            collateralQuantity={collateralQuantity}
                            collateralCurrentMarketPrice={collateralCurrentMarketPrice}
                            collateralPercent={collateralPercent}
                          />
                        )}
                      </div>
                    )}

                    {/* Rate Details */}
                    <div data-tour="trade-form-section-rate">
                      <SectionHeader title="Rate Details" section="rateDetails" />
                      {sectionStates.rateDetails && (
                        <RateDetailsSection
                          form={form}
                          isSubmitted={isSubmitted}
                          selectedInventoryItem={selectedInventoryItem}
                          rateType={rateType}
                          rateEntryMode={rateEntryMode}
                          securityCurrency={securityCurrency}
                          isCrossCurrency={isCrossCurrency}
                          effectiveSettlementCurrency={effectiveSettlementCurrency}
                          totalRate={totalRate}
                          calculatedValues={calculatedValues}
                          benchmarkRates={benchmarkRates}
                          benchmarkMeta={benchmarkMeta}
                        />
                      )}
                    </div>

                    {/* Position Dates */}
                    <div data-tour="trade-form-section-dates">
                      <SectionHeader title="Position Dates" section="positionDates" />
                      {sectionStates.positionDates && (
                        <PositionDatesSection
                          form={form}
                          isSubmitted={isSubmitted}
                          isFormDisabled={isFormDisabled}
                          tradeDuration={tradeDuration}
                          settlementDate={settlementDate}
                          selectedInventoryItem={selectedInventoryItem}
                          securityCurrency={securityCurrency}
                          calculatedValues={calculatedValues}
                          tradeDateHoliday={tradeDateHoliday}
                          settlementDateHoliday={settlementDateHoliday}
                        />
                      )}
                    </div>

                    {/* Settlement Details */}
                    <div data-tour="trade-form-section-settlement">
                      <SectionHeader title="Settlement Details" section="settlementDetails" />
                      {sectionStates.settlementDetails && (
                        <SettlementDetailsSection
                          form={form}
                          isSubmitted={isSubmitted}
                          isFormDisabled={isFormDisabled}
                          securityCurrency={securityCurrency}
                          effectiveSettlementCurrency={effectiveSettlementCurrency}
                          isCrossCurrency={isCrossCurrency}
                          isCashMatchingMode={isCashMatchingMode}
                          isUsingFallbackRates={isUsingFallbackRates}
                          deliveryType={deliveryType}
                          settlementVenue={settlementVenue}
                          counterpartyName={counterpartyName}
                          calculatedValues={calculatedValues}
                          fxRateWarning={fxRateWarning}
                        />
                      )}
                    </div>

                    {/* Validation summary. Surfaced live once a security is selected
                        (the form is inert before that), not gated behind a submit attempt:
                        both soft warnings (weekend dates, FX out-of-band, cross-currency)
                        and blocking errors show as soon as the triggering value is entered
                        (LR-452), mirroring the inline per-field messages. Warnings never
                        block submit; the box turns amber when only warnings are present. */}
                    {selectedInventoryItem && validationErrors.length > 0 && (
                      <div
                        aria-live="polite"
                        className={clsx('p-4 border-y', hasBlockingValidationError ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200')}
                      >
                        <h4 className={clsx('text-sm font-semibold mb-2', hasBlockingValidationError ? 'text-red-900' : 'text-yellow-900')}>
                          {hasBlockingValidationError ? 'Validation Issues' : 'Warnings'}
                        </h4>
                        <ul className="list-disc list-inside space-y-1">
                          {validationErrors.map((error, idx) => (
                            <li key={idx} className={clsx('text-xs', error.severity === 'error' ? 'text-red-700' : 'text-yellow-700')}>
                              {error.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Trade Summary */}
                    <div data-tour="trade-form-section-summary">
                      <SectionHeader title="Trade Summary" section="tradeSummary" />
                      {sectionStates.tradeSummary && (
                        <TradeSummarySection
                          securityCurrency={securityCurrency}
                          effectiveSettlementCurrency={effectiveSettlementCurrency}
                          isCrossCurrency={isCrossCurrency}
                          calculatedValues={calculatedValues}
                          showClosingSettlement={showClosingSettlement}
                        />
                      )}
                    </div>

                    {/* Comments */}
                    <div className="p-6">
                      <label className={labelClassName}>
                        Comments
                      </label>
                      <form.Field name="comments">
                        {(field) => (
                          <>
                            <textarea
                              value={field.state.value || ''}
                              onChange={(e) => field.handleChange(e.target.value || undefined)}
                              rows={3}
                              disabled={!selectedInventoryItem}
                              className={clsx(inputClassName, !selectedInventoryItem && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                              placeholder="Optional comments..."
                            />
                          </>
                        )}
                      </form.Field>
                    </div>
                  </div>

                  {/* Footer */}
                  <div data-tour="trade-form-actions" className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClose}
                      disabled={isCreating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={isCreating || !selectedInventoryItem}
                      onClick={async (e) => {
                        setIsSubmitted(true);
                        e.preventDefault();
                        e.stopPropagation();
                        await form.handleSubmit();
                      }}
                    >
                      {isCreating ? 'Creating...' : 'Create Trade'}
                    </Button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
    )}
    <CloseConfirmationDialog
      show={showCloseConfirmation}
      onCancel={() => setShowCloseConfirmation(false)}
      onConfirm={() => {
        setShowCloseConfirmation(false);
        handleCloseWithoutConfirmation();
      }}
    />
    </>
  );
};
